import { AiOutlined } from '@duxton-web/icons';
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Empty,
  Flex,
  Modal,
  Select,
  Spin,
  Tag,
  Typography,
} from '@duxton-web/ui';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';

import type { GenerationConfigSource } from './ConfigContentGeneration.helpers';
import { langLabel } from './adapters';
import { PRODUCT_STATUS_TAG, contentConfigModalStyle } from './theme';

interface Props {
  audienceListFailed: boolean;
  audienceListLoading: boolean;
  audiences: ContentGenerationSetupAudienceOption[];
  initialAudienceKeys: string[];
  initialLanguage: string;
  initialObjectiveID: string;
  initialSource?: GenerationConfigSource;
  isRegeneration: boolean;
  languages: string[];
  objectives: ContentGenerationSetupObjectiveOption[];
  open: boolean;
  sources?: ContentGenerationSetupSourceOption[];
  onCancel: () => void;
  onGenerate: (
    audienceKeys: string[],
    dateRange: [Dayjs, Dayjs],
    language: string,
    objectiveID: string | undefined,
    source: GenerationConfigSource,
  ) => void;
  onRetryAudiences: () => void;
  onSourceChange?: (source: GenerationConfigSource) => void;
}

export interface ContentGenerationSetupAudienceOption {
  key: string;
  label: string;
  whitelisted: boolean;
}

export interface ContentGenerationSetupObjectiveOption {
  id: string;
  label: string;
}

// Which version of the Product Offering configuration to generate from.
export interface ContentGenerationSetupSourceOption {
  key: GenerationConfigSource;
  label: string;
  tone: 'active' | 'pending' | 'draft';
  description: string;
}

const nextSevenDays = (): [Dayjs, Dayjs] => [
  dayjs().add(1, 'day'),
  dayjs().add(7, 'day'),
];
const initialDateRange = (): [Dayjs | null, Dayjs | null] => nextSevenDays();
const EMPTY_AUDIENCE_KEYS: string[] = [];
const EMPTY_SOURCE_OPTIONS: ContentGenerationSetupSourceOption[] = [];

const sourceFromOptions = (
  sources: ContentGenerationSetupSourceOption[],
  initialSource: GenerationConfigSource,
): GenerationConfigSource | '' => {
  if (sources.length === 0) return '';
  if (sources.some((source) => source.key === initialSource)) return initialSource;
  return sources[0].key;
};

const objectiveIDFromOptions = (
  objectives: ContentGenerationSetupObjectiveOption[],
  initialObjectiveID: string,
) => {
  if (objectives.length === 0) return '';
  if (initialObjectiveID && objectives.some((objective) => objective.id === initialObjectiveID)) {
    return initialObjectiveID;
  }
  if (objectives.length === 1) return objectives[0].id;
  return '';
};

const ContentGenerationSetupModal = ({
  audienceListFailed,
  audienceListLoading,
  audiences,
  initialAudienceKeys,
  initialLanguage,
  initialObjectiveID,
  initialSource = 'active',
  isRegeneration,
  languages,
  objectives,
  open,
  sources = EMPTY_SOURCE_OPTIONS,
  onCancel,
  onGenerate,
  onRetryAudiences,
  onSourceChange,
}: Props) => {
  const [selectedAudienceKeys, setSelectedAudienceKeys] = useState(EMPTY_AUDIENCE_KEYS);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [selectedObjectiveID, setSelectedObjectiveID] = useState('');
  const [selectedSource, setSelectedSource] = useState<GenerationConfigSource | ''>('');
  const [dateRange, setDateRange] = useState(initialDateRange());
  const hasUserEditedSelection = useRef(false);

  // One effect per field, each depending only on what that field derives from.
  // Sharing one effect made any dependency's identity change (the parent
  // rebuilds these arrays every render) reset unrelated selections.
  useEffect(() => {
    if (!open) return;
    const availableAudienceKeys = new Set(audiences.map((audience) => audience.key));
    hasUserEditedSelection.current = false;
    setSelectedAudienceKeys(initialAudienceKeys.filter((audienceKey) =>
      availableAudienceKeys.has(audienceKey)));
    // `audiences` is read but deliberately not a dependency: it arrives async
    // and the validity effect below reapplies the initial keys once it does.
    // Re-running here would also clear hasUserEditedSelection on every refetch.
  }, [initialAudienceKeys, open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    let defaultLanguage = languages[0] || '';
    if (languages.includes('en')) defaultLanguage = 'en';
    if (languages.includes(initialLanguage)) defaultLanguage = initialLanguage;
    setSelectedLanguage(defaultLanguage);
  }, [initialLanguage, languages, open]);

  useEffect(() => {
    if (!open) return;
    setDateRange(nextSevenDays());
  }, [open]);

  // Drop the chosen version and objective on close so reopening starts from the
  // defaults rather than a stale pick.
  useEffect(() => {
    if (open) return;
    setSelectedSource('');
    setSelectedObjectiveID('');
  }, [open]);

  // Keep the choice valid when the available versions change under the modal
  // (a pending change approved or recalled while it is open).
  useEffect(() => {
    if (!open) return;
    setSelectedSource((currentSource) => {
      if (currentSource && sources.some((source) => source.key === currentSource)) {
        return currentSource;
      }
      return sourceFromOptions(sources, initialSource);
    });
  }, [initialSource, open, sources]);

  useEffect(() => {
    if (!open) {
      hasUserEditedSelection.current = false;
      return;
    }
    const availableAudienceKeySet = new Set(audiences.map((audience) => audience.key));
    const initialSelection = initialAudienceKeys.filter((audienceKey) =>
      availableAudienceKeySet.has(audienceKey));

    setSelectedAudienceKeys((currentSelection) => {
      const validSelection = currentSelection.filter((audienceKey) =>
        availableAudienceKeySet.has(audienceKey));

      if (validSelection.length !== currentSelection.length) {
        return validSelection;
      }

      if (!hasUserEditedSelection.current && currentSelection.length === 0) {
        return initialSelection;
      }

      return currentSelection;
    });
  }, [audiences, initialAudienceKeys, open]);

  useEffect(() => {
    if (!open) return;
    setSelectedObjectiveID((currentObjectiveID) => {
      if (!objectives.length) return '';
      if (currentObjectiveID && objectives.some((objective) => objective.id === currentObjectiveID)) {
        return currentObjectiveID;
      }
      return objectiveIDFromOptions(objectives, initialObjectiveID);
    });
  }, [initialObjectiveID, objectives, open]);

  const allSelected = audiences.length > 0
    && selectedAudienceKeys.length === audiences.length;
  const someSelected = selectedAudienceKeys.length > 0 && !allSelected;
  const ready = selectedAudienceKeys.length > 0
    && !audienceListLoading
    && !audienceListFailed
    && Boolean(selectedLanguage)
    && (sources.length === 0 || Boolean(selectedSource))
    && (objectives.length === 0 || Boolean(selectedObjectiveID))
    && Boolean(dateRange[0] && dateRange[1]);

  const submit = () => {
    if (!dateRange[0] || !dateRange[1] || !selectedLanguage || selectedAudienceKeys.length === 0) {
      return;
    }
    if (objectives.length > 0 && !selectedObjectiveID) return;
    if (sources.length > 0 && !selectedSource) return;
    onGenerate(
      selectedAudienceKeys,
      [dateRange[0], dateRange[1]],
      selectedLanguage,
      selectedObjectiveID || undefined,
      selectedSource || initialSource,
    );
  };

  const selectedSourceOption = sources.find((source) => source.key === selectedSource);

  useEffect(() => {
    if (!open || !selectedSource || !onSourceChange) return;
    onSourceChange(selectedSource);
  }, [onSourceChange, open, selectedSource]);

  const renderAudienceSelection = () => {
    if (audienceListLoading) {
      return (
        <Flex align="center" gap={8} className="cc-generation-audience-state">
          <Spin size="small" />
          <Typography.Body level={2} type="secondary">Loading audiences...</Typography.Body>
        </Flex>
      );
    }

    if (audienceListFailed) {
      return (
        <Flex vertical gap={8}>
          <Alert
            type="error"
            showIcon
            message="Audience list could not be loaded."
          />
          <Flex>
            <Button size="small" onClick={onRetryAudiences}>Retry audience list</Button>
          </Flex>
        </Flex>
      );
    }

    if (audiences.length === 0) {
      return <Empty description="No active audiences are available for this country." />;
    }

    return (
      <div className="cc-generation-attribute-picker">
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(event) => {
            hasUserEditedSelection.current = true;
            setSelectedAudienceKeys(
              event.target.checked
                ? audiences.map((audience) => audience.key)
                : [],
            );
          }}
        >
          Select all
        </Checkbox>
        <div className="cc-generation-attribute-divider" />
        <Checkbox.Group
          value={selectedAudienceKeys}
          onChange={(values: string[]) => {
            hasUserEditedSelection.current = true;
            setSelectedAudienceKeys(values);
          }}
          className="cc-generation-attribute-options"
        >
          {audiences.map((audience) => (
            <Checkbox key={audience.key} value={audience.key}>
              <Flex align="center" gap={8} wrap="wrap">
                <span>{audience.label}</span>
                <Tag color={audience.whitelisted ? 'success' : 'warning'}>
                  {audience.whitelisted ? 'Whitelisted' : 'Non-whitelisted'}
                </Tag>
              </Flex>
            </Checkbox>
          ))}
        </Checkbox.Group>
      </div>
    );
  };

  return (
    <Modal
      title={(
        <Flex align="center" gap={8}>
          <AiOutlined />
          <span>{isRegeneration ? 'Regenerate content' : 'Generate content'}</span>
        </Flex>
      )}
      open={open}
      onCancel={onCancel}
      onOk={submit}
      okText={isRegeneration ? 'Regenerate content' : 'Generate content'}
      okButtonProps={{ disabled: !ready }}
      width={800}
      style={contentConfigModalStyle}
      className="cc-modal cc-generation-setup-modal"
      destroyOnClose
    >
      <Flex vertical gap="large">
        {sources.length > 0 && (
          <Flex vertical gap={8}>
            <Typography.Body level={2} strong>Configuration</Typography.Body>
            <Typography.Metadata type="secondary">
              Choose which version of this product offering to generate from.
            </Typography.Metadata>
            <Select
              value={selectedSource || undefined}
              onChange={setSelectedSource}
              options={sources.map((source) => ({
                value: source.key,
                label: (
                  <Tag color={(PRODUCT_STATUS_TAG[source.tone] || {}).color}>{source.label}</Tag>
                ),
              }))}
              style={{ width: '100%' }}
              placeholder="Select configuration"
            />
            {selectedSourceOption && (
              <Typography.Metadata type="secondary">
                {selectedSourceOption.description}
              </Typography.Metadata>
            )}
          </Flex>
        )}

        <Flex vertical gap={8}>
          <Typography.Body level={2} strong>Language</Typography.Body>
          <Select
            value={selectedLanguage}
            onChange={setSelectedLanguage}
            options={languages.map((language) => ({
              value: language,
              label: langLabel(language),
            }))}
            style={{ width: '100%' }}
          />
        </Flex>

        {objectives.length > 0 && (
          <Flex vertical gap={8}>
            <Typography.Body level={2} strong>Objective</Typography.Body>
            <Typography.Metadata type="secondary">
              Select one objective for this content generation run.
            </Typography.Metadata>
            <Select
              value={selectedObjectiveID || undefined}
              onChange={setSelectedObjectiveID}
              options={objectives.map((objective) => ({
                value: objective.id,
                label: objective.label,
              }))}
              style={{ width: '100%' }}
              placeholder="Select objective"
            />
          </Flex>
        )}

        <Flex vertical gap={8}>
          <Typography.Body level={2} strong>Audiences</Typography.Body>
          <Typography.Metadata type="secondary">
            Mystique will generate a separate set of results for each selected audience.
          </Typography.Metadata>
          {renderAudienceSelection()}
        </Flex>

        <Flex vertical gap={8}>
          <Typography.Body level={2} strong>Date range</Typography.Body>
          <DatePicker.RangePicker
            value={dateRange}
            onChange={(dates) => {
              setDateRange(dates ? [dates[0], dates[1]] : [null, null]);
            }}
            disabledDate={(date) => date && date.isBefore(dayjs(), 'day')}
            format="MM/DD/YYYY"
            placeholder={['Start date', 'End date']}
            style={{ width: '100%' }}
          />
        </Flex>
      </Flex>
    </Modal>
  );
};

export default ContentGenerationSetupModal;
